import { generateParams } from './jsonData';
import { isDev } from './utils';

const inFlight = new Map();

// sort object keys for stable ordering
function stableStringify(obj) {
  return JSON.stringify(obj, (_k, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const ordered = {};
      Object.keys(v)
        .sort()
        .forEach((k) => (ordered[k] = v[k]));
      return ordered;
    }
    return v;
  });
}

/**
 * Fetcher for NQ endpoints.
 * params:
 * - pageKey: NQ pageKey (pages, feeds, etc.)
 * - payload: Plain object to merge into endpoint params (for product detail requests) - Optional
 * - options: { signal }
 */
async function fetchNq({
  pageKey,
  payload = null,
  options = {},
  type = 'feed'
}) {
  const { signal } = options;
  const dedupeKey = payload
    ? `${type}:${pageKey}:${stableStringify(payload)}`
    : `${type}:${pageKey}`;

  // If there's an identical request in-flight, reuse it.
  if (inFlight.has(dedupeKey)) {
    if (isDev) {
      try {
        console.log('[dev] fetchNq: reusing in-flight promise:', dedupeKey);
      } catch (e) {}
    }
    return inFlight.get(dedupeKey);
  }

  const p = (async () => {
    const { endPointUrl, endPointParams, endPointUrlDNS } = generateParams(
      pageKey,
      '0',
      type
    );

    // Merge payload into endPointParams (either FormData or URLSearchParams)
    let body;
    if (endPointParams instanceof FormData) {
      const fd = new FormData();
      for (const [k, v] of endPointParams.entries()) fd.append(k, v);
      if (payload && typeof payload === 'object') {
        Object.keys(payload).forEach((k) => fd.append(k, payload[k]));
      }
      body = fd;
    } else if (typeof endPointParams === 'object') {
      const merged = { ...endPointParams };
      if (payload && typeof payload === 'object') {
        Object.keys(payload).forEach((k) => (merged[k] = payload[k]));
      }
      body = new URLSearchParams(merged).toString();
    } else {
      // endPointParams not object - fallback to payload only
      body =
        payload && typeof payload === 'object'
          ? new URLSearchParams(payload).toString()
          : endPointParams;
    }

    const doFetch = async (urlToUse) => {
      const resp = await fetch(urlToUse, {
        method: 'POST',
        headers:
          body instanceof FormData
            ? undefined
            : { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
        signal
      });
      if (!resp.ok)
        throw new Error(`Network response was not ok: ${resp.status}`);
      const text = await resp.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        data = text;
      }

      const isEmptyObject =
        data && typeof data === 'object' && Object.keys(data).length === 0;
      const isNoData = !data || isEmptyObject;
      if (isNoData) throw new Error('Empty response');
      return data;
    };

    // Try primary endpoint first, then DNS fallback if provided
    try {
      return await doFetch(endPointUrl);
    } catch (errPrimary) {
      if (endPointUrlDNS) {
        try {
          if (isDev)
            console.warn(
              '[dev] fetchNq: primary failed, trying DNS',
              errPrimary.message
            );
          return await doFetch(endPointUrlDNS);
        } catch (errDns) {
          const msg = `Primary and DNS endpoints failed: ${errPrimary.message}; ${errDns.message}`;
          throw new Error(msg);
        }
      }
      throw errPrimary;
    }
  })();

  if (isDev) {
    try {
      console.log('[dev] fetchNq: dedupeKey (new request):', dedupeKey);
    } catch (e) {}
  }

  inFlight.set(dedupeKey, p);
  p.finally(() => inFlight.delete(dedupeKey));
  return p;
}

export async function fetchFeed(pageKey, options = {}, type = 'feed') {
  // options is optional and forwarded to fetchNq (e.g., { signal })
  return fetchNq({ pageKey, options, type });
}

export async function fetchProductDetails(
  pageKey,
  productPayload,
  options = {},
  type = 'feed'
) {
  return fetchNq({ pageKey: pageKey, payload: productPayload, options, type });
}

export default fetchNq;
